import React, { useState } from "react";

import { useVideoDirection } from "../../../../services/useVideoDirection";

/**
 * The visual direction, and the video prompts, on the step that submits.
 *
 * See `services/useVideoDirection` for what was missing and why. In short: the
 * direction existed on every section and was never shown here, and the prompts
 * were built inside the submit and stored where nothing read them.
 *
 * THE THREE OUTCOMES ARE DISTINGUISHABLE, which is the whole point of showing
 * this at all (rule 10): `ok` means every segment translated; `degraded` means
 * the set was built but something named in the warnings fell back or was lost —
 * and the warnings are printed, not counted; `error` means nothing usable was
 * produced and the submit would refuse. A screen that showed the same panel for
 * all three would be worse than no panel.
 */
export default function VideoDirectionPanel({ script }) {
  const isAi = script.creative_type === "AI";
  // FOUNDER REQUEST 2026-09-10: edit and refine, per clip. Same two gestures as
  // the hooks — the operator's own words, or the Specialist rewriting from a
  // note, with only that clip changing.
  const [editClip, setEditClip] = useState(null);      // segment_index
  const [editText, setEditText] = useState("");
  const [refineClipIdx, setRefineClipIdx] = useState(null);
  const [refineText, setRefineText] = useState("");
  const V = useVideoDirection({
    scriptId: script.script_id,
    productionId: script.production_id || null,
  });

  const p = V.prompts;

  return (
    <div className="ws-vd" data-testid={`ws-vd-${script.script_id}`}>
      {V.state === "loading" ? (
        <p className="dim" data-testid="ws-vd-loading">Reading the visual direction…</p>
      ) : null}

      {V.state === "failed" ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-vd-failed">
          <p>The visual direction could not be read: {V.error}</p>
          <p className="dim">
            This is a failed read, not a script without direction.
          </p>
          <button type="button" className="btn-ghost btn-sm" onClick={V.retry}
                  data-testid="ws-vd-retry">Try again</button>
        </div>
      ) : null}

      {V.state === "ok" && V.direction ? (
        <>
          <p className="dim" data-testid="ws-vd-count">
            Visual direction on {V.direction.with_direction} of{" "}
            {V.direction.total_sections} section
            {V.direction.total_sections === 1 ? "" : "s"}.
          </p>
          <ul className="ws-vd-sections">
            {(V.direction.sections || []).map((sec, i) => (
              <li key={`${sec.section}-${i}`} data-testid={`ws-vd-section-${i}`}>
                <span className="ws-vd-name">{sec.section}</span>
                {sec.timing ? <span className="dim"> {sec.timing}</span> : null}
                {sec.has_direction ? (
                  <p className="ws-vd-text">{sec.visual_direction}</p>
                ) : (
                  <p className="ws-vd-text state-warn"
                     data-testid={`ws-vd-nodirection-${i}`}>
                    No visual direction written for this section — whoever builds
                    it has the words and not the shot.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* ── The video prompts ──────────────────────────────────────────────── */}
      {isAi ? (
        <div className="ws-vd-prompts">
          {script.production_id ? (
            <button type="button" className="btn-ghost btn-sm"
                    disabled={V.promptState === "loading"}
                    onClick={V.loadStored}
                    data-testid={`ws-vd-stored-${script.script_id}`}>
              {V.promptState === "loading" ? "Reading…" : "Show the prompts that were sent"}
            </button>
          ) : (
            <button type="button" className="btn-ghost btn-sm"
                    disabled={V.promptState === "loading"}
                    onClick={V.preview}
                    data-testid={`ws-vd-preview-${script.script_id}`}>
              {V.promptState === "loading" ? "Building the prompts…" : "Show the video prompts"}
            </button>
          )}
          <p className="dim">
            {script.production_id
              ? "Read from the production record — this is what the brief carried."
              : "Builds the prompts this script would generate — a text model "
                + "writes them, so it takes a few seconds. Nothing is saved, and "
                + "nothing is submitted to any video model."}
          </p>

          {V.promptState === "failed" ? (
            <p className="ws-step-note state-error" role="alert"
               data-testid="ws-vd-prompt-failed">
              The prompts could not be read: {V.promptError}
            </p>
          ) : null}

          {V.promptState === "ok" && p ? (
            <div data-testid="ws-vd-prompt-set">
              <p className="dim" data-testid="ws-vd-prompt-source">
                {V.source === "stored"
                  ? "As submitted to production."
                  : "A preview. Not saved, not submitted."}
                {" "}State: <strong>{p.state}</strong>.
              </p>

              {/* WHO WROTE THESE. Founder ruling 2026-09-10: the prompts go
                  through the Specialist that holds the art-direction Skill.
                  Shown here because "a Specialist wrote this, using that Skill"
                  is a claim the operator would otherwise have to take on trust
                  — and the run id is what makes it checkable. */}
              {p.written_by?.path === "specialist" ? (
                <p className="dim" data-testid="ws-vd-written-by">
                  Written by {p.written_by.role || "the Specialist"} using the{" "}
                  <strong>{p.written_by.skill}</strong> Skill
                  {p.written_by.run_id ? ` · run ${p.written_by.run_id}` : ""}
                </p>
              ) : p.written_by?.path ? (
                <p className="ws-step-note state-warn" data-testid="ws-vd-written-by-fallback">
                  These were NOT written by the art-direction Specialist —
                  {" "}{p.written_by.reason || p.written_by.path}.
                </p>
              ) : null}

              {V.source === "stale" ? (
                <p className="ws-step-note state-warn" data-testid="ws-vd-stale">
                  The script has changed since these prompts were written, so
                  they describe an earlier version of the ad. Press "Show the
                  video prompts" to rebuild them — any hand edits will be lost,
                  which is why this says so before you submit.
                </p>
              ) : p.stored || V.source === "draft" ? (
                <p className="dim" data-testid="ws-vd-will-ship">
                  These are the prompts the brief will carry when you submit.
                </p>
              ) : null}

              {p.state === "not_applicable" ? (
                <p className="ws-step-note" data-testid="ws-vd-not-applicable">{p.why}</p>
              ) : null}

              {p.state === "none" ? (
                <p className="ws-step-note" data-testid="ws-vd-prompt-none">{p.why}</p>
              ) : null}

              {p.state === "error" ? (
                <p className="ws-step-note state-error" role="alert"
                   data-testid="ws-vd-prompt-error">
                  No prompts were produced: {p.error} Submitting this script as
                  AI Video would be refused for the same reason.
                </p>
              ) : null}

              {/* PRINTED, NOT COUNTED. On the Founder's own submitted script one
                  of these says the middle of the ad reached no clip prompt. A
                  number would have hidden it. */}
              {(p.warnings || []).length ? (
                <ul className="ws-step-note state-warn" data-testid="ws-vd-prompt-warnings">
                  {p.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              ) : null}

              {(p.segments || []).map((seg) => (
                <div key={seg.segment_index} className="ws-vd-segment"
                     data-testid={`ws-vd-segment-${seg.segment_index}`}>
                  <p className="ws-vd-name">
                    Clip {Number(seg.segment_index) + 1} · {seg.label}
                  </p>
                  {seg.original_visual_direction ? (
                    <p className="dim">Direction: {seg.original_visual_direction}</p>
                  ) : null}
                  {(seg.translated_veo_prompt || "").trim() ? (
                    <pre className="ws-vd-prompt">{seg.translated_veo_prompt}</pre>
                  ) : (
                    <p className="ws-step-note state-warn"
                       data-testid={`ws-vd-empty-prompt-${seg.segment_index}`}>
                      This clip produced an EMPTY prompt. Do not generate it from
                      this document.
                    </p>
                  )}

                  {/* WHOSE WORDS THESE ARE. A prompt a person wrote and one the
                      Specialist wrote must not read the same — the brief tells
                      the editor not to "correct" a deliberate edit. */}
                  {seg.source === "OPERATOR_EDIT" ? (
                    <span className="ws-chip" data-testid={`ws-vd-clip-edited-${seg.segment_index}`}>
                      your words
                    </span>
                  ) : seg.source === "SPECIALIST_REFINED" ? (
                    <span className="ws-chip" data-testid={`ws-vd-clip-refined-${seg.segment_index}`}>
                      refined
                    </span>
                  ) : null}

                  {/* Editing needs a saved draft. A preview that stored nothing
                      would be an edit with nowhere to go, so the controls only
                      appear once there is one. */}
                  {p.stored || V.source === "draft" || p.has_draft ? (
                    <div className="ws-vd-clip-actions">
                      <button type="button" className="btn-ghost btn-sm"
                              onClick={() => {
                                setRefineClipIdx(refineClipIdx === seg.segment_index
                                  ? null : seg.segment_index);
                                setRefineText("");
                                setEditClip(null);
                              }}
                              data-testid={`ws-vd-refine-btn-${seg.segment_index}`}>
                        Refine
                      </button>
                      <button type="button" className="btn-ghost btn-sm"
                              onClick={() => {
                                if (editClip === seg.segment_index) {
                                  setEditClip(null);
                                  return;
                                }
                                setEditClip(seg.segment_index);
                                setRefineClipIdx(null);
                                // Seeded from the prompt, so an untouched field
                                // round-trips unchanged.
                                setEditText(seg.translated_veo_prompt || "");
                              }}
                              data-testid={`ws-vd-edit-btn-${seg.segment_index}`}>
                        Edit text
                      </button>
                    </div>
                  ) : null}

                  {refineClipIdx === seg.segment_index ? (
                    <div className="ws-vd-clip-drawer"
                         data-testid={`ws-vd-refine-drawer-${seg.segment_index}`}>
                      <p className="dim">
                        Say what to change. The Specialist rewrites this clip
                        only, and is shown the others so the subject stays the
                        same across the ad.
                      </p>
                      <textarea rows={2} value={refineText}
                                onChange={(e) => setRefineText(e.target.value)}
                                placeholder="Warmer light, and put her at the kitchen table."
                                data-testid={`ws-vd-refine-text-${seg.segment_index}`} />
                      <div className="ws-vd-clip-foot">
                        <button type="button" className="btn-ghost btn-sm"
                                onClick={() => { setRefineClipIdx(null); setRefineText(""); }}
                                data-testid={`ws-vd-refine-cancel-${seg.segment_index}`}>
                          Discard
                        </button>
                        <button type="button" className="btn-primary btn-sm"
                                disabled={refineText.trim().length < 3
                                          || V.promptState === "loading"}
                                onClick={async () => {
                                  const ok = await V.refineClip(seg.segment_index, refineText);
                                  if (ok) { setRefineClipIdx(null); setRefineText(""); }
                                }}
                                data-testid={`ws-vd-refine-save-${seg.segment_index}`}>
                          {V.promptState === "loading" ? "Rewriting…" : "Refine this clip"}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {editClip === seg.segment_index ? (
                    <div className="ws-vd-clip-drawer"
                         data-testid={`ws-vd-edit-drawer-${seg.segment_index}`}>
                      <p className="dim">
                        Your words, used exactly as written — no model. This is
                        what the brief will carry.
                      </p>
                      <textarea rows={6} value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                data-testid={`ws-vd-edit-text-${seg.segment_index}`} />
                      <div className="ws-vd-clip-foot">
                        <button type="button" className="btn-ghost btn-sm"
                                onClick={() => { setEditClip(null); setEditText(""); }}
                                data-testid={`ws-vd-edit-cancel-${seg.segment_index}`}>
                          Discard
                        </button>
                        <button type="button" className="btn-primary btn-sm"
                                disabled={!editText.trim()}
                                onClick={async () => {
                                  const ok = await V.editClip(seg.segment_index, editText);
                                  if (ok) { setEditClip(null); setEditText(""); }
                                }}
                                data-testid={`ws-vd-edit-save-${seg.segment_index}`}>
                          Save my words
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}

              {p.direction ? (
                <details data-testid="ws-vd-whole-direction">
                  <summary>Whole-script direction</summary>
                  <pre className="ws-vd-prompt">{p.direction}</pre>
                </details>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <p className="dim" data-testid={`ws-vd-nonai-${script.script_id}`}>
          Video prompts are produced for AI Video only. A{" "}
          {script.creative_type || "script with no type yet"} brief carries the
          direction above for a person to work from.
        </p>
      )}
    </div>
  );
}
